import { convertToTimeZone } from 'date-fns-timezone';
import moment from 'moment-timezone';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

import { WORK_DAY_START_HOURS } from './constants';
import { getDeviceInfo } from './device-info';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(tz);

export const MONTH_NAME_DATE_YEAR_FORMAT = 'MMM DD YYYY';
export const EVENT_DATE_FORMAT = 'MMM DD';
export const EVENT_TIME_FORMAT = 'h:mma';
export const EVENT_DATE_TIME_FORMAT = `${EVENT_DATE_FORMAT}, ${EVENT_TIME_FORMAT}`;

export const getDayRange = (date: string, timezone: string) => ({
  start: moment.tz(date, timezone).startOf('day').toISOString(),
  end: moment.tz(date, timezone).endOf('day').toISOString()
});

export const secondsToMinutes = (seconds: number) =>
  Math.floor(seconds / 60) + ':' + ('0' + Math.floor(seconds % 60)).slice(-2);

type DateInput = Parameters<typeof convertToTimeZone>[0];

export const getLocalDateTimeTZ = (date: DateInput, timeZone: string) => convertToTimeZone(date, { timeZone });

export const getLocalDateTimeOffsetTZ = (timeZone: string) => moment.tz(moment(), timeZone).utcOffset();

export const renderDayName = (date: string) => {
  const eventDate = new Date(date);
  const today = new Date().getDate();

  if (eventDate.getDate() === today) {
    return 'TODAY';
  } else {
    return `${eventDate.getMonth() + 1}/${eventDate.getDate()}/${eventDate.getFullYear().toString().substr(-2)}`;
  }
};

export const getWorkingDay = (
  workStartHours: number = WORK_DAY_START_HOURS,
  timeZone: string = getDeviceInfo().timezone,
  date: string | moment.Moment = moment()
) => {
  const currentDateTime = moment.tz(date, timeZone);
  const workStartDateTime = moment.tz(moment(), timeZone).startOf('day').add(workStartHours, 'hours');

  if (currentDateTime.isBefore(workStartDateTime)) {
    currentDateTime.add(-1, 'day');
  }

  return currentDateTime;
};

export const getWorkingDayStartDateTime = (start: number = WORK_DAY_START_HOURS, timeZone?: string, date?: string) => {
  if (date && timeZone) {
    return moment.tz(`${date} 00:00`, timeZone).add(start, 'hours').utc().toISOString();
  }
  return getWorkingDay(start, timeZone).hours(start).minutes(0).seconds(0).milliseconds(0).toISOString();
};

export const getWorkingDayEndDateTime = (start: number = WORK_DAY_START_HOURS, timeZone?: string, date?: string) => {
  if (date && timeZone) {
    return moment.tz(`${date} 00:00`, timeZone).endOf('day').add(start, 'hours').utc().toISOString();
  }
  return getWorkingDay(start, timeZone)
    .add(1, 'day')
    .hours(start - 1)
    .minutes(59)
    .seconds(59)
    .milliseconds(999)
    .toISOString();
};

export const getXHoursAgoDateTime = (hours: number) => {
  const nearestFiveMinsRoundedDown = Math.floor(moment().minute() / 5) * 5;
  return moment().milliseconds(0).seconds(0).minutes(nearestFiveMinsRoundedDown).subtract(hours, 'hour').toISOString();
};

export const renderEventTime = (dateTimeString: string, timeZone: string) => {
  const newDate = getLocalDateTimeTZ(new Date(dateTimeString), timeZone);
  return renderLocalEventTime(newDate);
};

export const renderEventDate = (dateTimeString: string, timezone: string) => {
  const newDateTime = getLocalDateTimeTZ(new Date(dateTimeString), timezone);
  return dayjs(newDateTime).format(MONTH_NAME_DATE_YEAR_FORMAT);
};

export const getFormattedDateAndTime = (dateTimeString: string, timezone: string) => {
  const date = renderEventDate(dateTimeString, timezone);
  const time = renderEventTime(dateTimeString, timezone);

  return { date, time };
};

export function renderLocalEventTime(newDate: Date) {
  const hours = newDate.getHours();
  const minutes = newDate.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const localHours = hours % 12 || 12;

  const localMinutes = minutes < 10 ? '0' + minutes : minutes;

  return localHours + ':' + localMinutes + '' + ampm;
}

export const renderFriendlyEventDate = (dateTimeString: string, timeZone: string) => {
  const UTCDate = getLocalDateTimeTZ(new Date(dateTimeString), timeZone);
  return dayjs(UTCDate).format('MMM DD');
};

export const parseEventDateTime = (dateTimeString: string, timeZone: string, separator: string = ' ') => {
  return [renderFriendlyEventDate(dateTimeString, timeZone), renderEventTime(dateTimeString, timeZone)].join(separator);
};

export const formatDateOnly = (date: Date | string) => dayjs(date).format('MM/DD/YYYY');

export const formatDOB = (date: Date | string) => dayjs(date).format('MM/DD/YYYY');

export const formatDate = (date: Date | string) => dayjs(date).format('MM/DD/YY h:mma');

export const relativeToNow = (date: string) => dayjs.utc(date).fromNow();

export const getFormattedCurrentLocalDate = (timezone: string) =>
  getFormattedLocalDate(dayjs().toISOString(), timezone);

export const getFormattedLocalDate = (dateTime: string, timeZone: string) =>
  dayjs(getLocalDateTimeTZ(dayjs(dateTime).toDate(), timeZone)).format('MM/DD/YY h:mma');

export const formatStartDateTime = (startDateTime: string, timezone: string) => {
  const date = getLocalDateTimeTZ(new Date(startDateTime), timezone);
  return dayjs(date).format('dddd, MMMM D, YYYY');
};

const momentAbbreviations = {
  UK: 'GB',
  USA: 'US',
  CANADA: 'CANADA',
  PUERTO_RICO: 'PR'
};

export const timezones = (country: string) => {
  return moment.tz.zonesForCountry(momentAbbreviations[country] || country) || [];
};

export const isToday = (zuluDateTime: string, timezone: string): boolean => {
  return dayjs(zuluDateTime).tz(timezone).isSame(dayjs().tz(timezone), 'day');
};

export const isTomorrow = (zuluDateTime: string, timezone: string): boolean => {
  return dayjs(zuluDateTime).tz(timezone).subtract(1, 'day').isSame(dayjs().tz(timezone), 'day');
};

const Time = {
  renderDayName,
  renderFriendlyEventDate,
  renderEventTime,
  parseEventDateTime,
  secondsToMinutes,
  getWorkingDay,
  isToday,
  isTomorrow
};

export default Time;
